import moment from "moment-timezone";
import { nanoid } from "nanoid";

import * as date from "@/utils/date";

import * as customersApi from "../../../apiv2/customers";
import * as labelsApi from "../../../apiv2/labels";
import * as calendarApi from "../../../apiv2/planner/calendar";
import * as ordersApi from "../../../apiv2/planner/orders";
import * as workflowApi from "../../../apiv2/workflow";
// Two different alert systems with near-identical paths: `alertActions` is the app-wide bottom-left
// stack, `plannerAlertActions` is the planner's own top-centre one.
import * as alertActions from "../../alerts/alerts.actions";
import { handleError } from "../../utils/error";
import * as plannerAlertActions from "../alerts/alerts.actions";
import * as drawerActions from "../drawer/drawer.actions";
import * as drawerSelectors from "../drawer/drawer.selectors";
import { hasEmptiedServerFilterGroup } from "../filter.helpers";
import * as labelFilterSelectors from "../labelFilter/labelFilter.selectors";
import * as orderActions from "../orders/orders.actions";
import { PROGRESS_VALUES } from "../progressFilter/progressFilter.constants";
import * as progressFilterSelectors from "../progressFilter/progressFilter.selectors";
import * as requiredDateSelectors from "../requiredDateFilter/requiredDate.selectors";
import * as searchFilterSelectors from "../searchFilter/searchFilter.selectors";
import * as statusFilterSelectors from "../statusFilter/statusFilter.selectors";
import * as userFilterSelectors from "../userFilter/userFilter.selectors";

import {
  ADD_SCHEDULE_ENTRY,
  CLEAR_CONTEXT_MENU_ENTRY_UID,
  REMOVE_SCHEDULE_ENTRY,
  RESET,
  SET_ACTIVE_SCHEDULE,
  SET_CALENDAR_VIEW,
  SET_CONTEXT_MENU_ENTRY_UID,
  SET_DATA_IN_CALENDAR_ENTRY,
  SET_DELETE_SCOPE,
  SET_EDIT_SCOPE,
  SET_FROM_DATE,
  SET_INTITIAL_CUSTOM_EVENT_DATA,
  SET_IS_CREATING_SCHEDULE,
  SET_IS_FETCHING_LABELS,
  SET_IS_FETCHING_SCHEDULES,
  SET_IS_FETCHING_USERS,
  SET_LABELS,
  SET_ORDER_IN_CALENDAR,
  SET_SELECTED_USERS,
  SET_SHOW_CUSTOM_EVENT_MODAL,
  SET_SHOW_DELETE_CUSTOM_EVENT_WARNING_MODAL,
  SET_SHOW_EDIT_CUSTOM_EVENT_WARNING_MODAL,
  SET_TO_DATE,
  SET_USERS,
  UPDATE_ALL_SCHEDULE_ENTRIES,
  UPDATE_SCHEDULE_ENTRY,
} from "./calendar.actionTypes";
import * as selectors from "./calendar.selectors";

export const reset = () => (dispatch) => {
  dispatch({
    type: RESET,
  });
};

export const setIsFetchingUsers = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING_USERS,
    payload: status,
  });
};

export const setUsers = (data) => (dispatch) => {
  dispatch({
    type: SET_USERS,
    payload: data,
  });
};

export const setIsFetchingLabels = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING_LABELS,
    payload: status,
  });
};

export const setLabels = (data) => (dispatch) => {
  dispatch({
    type: SET_LABELS,
    payload: data,
  });
};

export const setIsFetchingSchedules = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING_SCHEDULES,
    payload: status,
  });
};

export const setOrderInCalendar = (data) => (dispatch) => {
  dispatch({
    type: SET_ORDER_IN_CALENDAR,
    payload: data,
  });
};

// Merges `patch` into the schedule entry with `uid` at reduce time, against whatever `schedules`
// currently holds. A no-op if the uid isn't present (e.g. it scrolled out of the loaded window).
export const updateScheduleEntry = (uid, patch) => (dispatch) => {
  dispatch({
    type: UPDATE_SCHEDULE_ENTRY,
    payload: { uid, patch },
  });
};

export const removeScheduleEntry = (uid) => (dispatch) => {
  dispatch({
    type: REMOVE_SCHEDULE_ENTRY,
    payload: uid,
  });
};

export const addScheduleEntry = (entry) => (dispatch) => {
  dispatch({
    type: ADD_SCHEDULE_ENTRY,
    payload: entry,
  });
};

// Applies `patch` to every schedule entry, or only those where `when.field` is truthy (e.g.
// clear-all only touches entries that already have an override). `when` is a plain object, not a
// function, so the action stays serializable.
export const updateAllScheduleEntries =
  (patch, when = null) =>
  (dispatch) => {
    dispatch({
      type: UPDATE_ALL_SCHEDULE_ENTRIES,
      payload: { patch, when },
    });
  };

export const setFromDate = (data) => (dispatch) => {
  dispatch({
    type: SET_FROM_DATE,
    payload: data,
  });
};

export const setToDate = (data) => (dispatch) => {
  dispatch({
    type: SET_TO_DATE,
    payload: data,
  });
};

export const setActiveSchedule = (data) => (dispatch) => {
  dispatch({
    type: SET_ACTIVE_SCHEDULE,
    payload: data,
  });
};

export const setContextMenuEntryUid = (uid, owner) => (dispatch) => {
  dispatch({
    type: SET_CONTEXT_MENU_ENTRY_UID,
    payload: uid ? { uid, owner } : null,
  });
};

export const clearContextMenuEntryUid = (owner) => (dispatch) => {
  dispatch({
    type: CLEAR_CONTEXT_MENU_ENTRY_UID,
    payload: owner,
  });
};

export const setIsCreatingSchedule = (data) => (dispatch) => {
  dispatch({
    type: SET_IS_CREATING_SCHEDULE,
    payload: data,
  });
};

export const setCalendarView = (data) => (dispatch) => {
  dispatch({
    type: SET_CALENDAR_VIEW,
    payload: data,
  });
};

export const setSelectedUsers = (data) => (dispatch) => {
  dispatch({
    type: SET_SELECTED_USERS,
    payload: data,
  });
};

export const setDataInCalendarEntry = (data) => (dispatch) => {
  dispatch({
    type: SET_DATA_IN_CALENDAR_ENTRY,
    payload: data,
  });
};

export const fetchUsers = () => async (dispatch) => {
  try {
    dispatch(setIsFetchingUsers(true));
    const response = await customersApi.getUsers({
      include_invites: false,
      with_inactive: false,
    })();

    dispatch(
      setUsers(
        response.data.map((d) => ({
          id: d.id,
          firstName: d.firstName,
          lastName: d.lastName,
          color: d.color,
        }))
      )
    );

    dispatch(setSelectedUsers(response.data.map((u) => u.id)));
  } catch (error) {
    handleError(error, dispatch);
  } finally {
    dispatch(setIsFetchingUsers(false));
  }
};

export const fetchLabels = () => async (dispatch) => {
  try {
    dispatch(setIsFetchingLabels(true));
    const response = await labelsApi.getLabels()();
    dispatch(setLabels(response.data));
  } catch (error) {
    handleError(error, dispatch);
  } finally {
    dispatch(setIsFetchingLabels(false));
  }
};

// `range` (optional): { fromDate, toDate } "YYYY-MM-DD" to fetch a range wider/narrower than Redux
// fromDate/toDate; for example, Month fetches the full visible grid (incl. spillover) while
// `config.date` stays a day inside the month. Omit it and the Redux fromDate/toDate are used
// (Day/Week).
// The visible date range for a view, derived from the anchor `fromDate`. A filter/search refetch
// calls fetchSchedules() with no range, so deriving it here keeps those refetches covering the whole
// month/week instead of collapsing to the anchor..toDate window (which was dropping entries).
export const getVisibleRange = (calendarView, fromDate) => {
  const anchor = moment(fromDate);
  if (calendarView === "week") {
    // isoWeek (Monday-first) to match the grid's `weekStartsOn: 1`. Plain `startOf("week")` follows
    // moment's default locale (Sunday), which left the refetch window a week off from the columns.
    return {
      fromDate: anchor.clone().startOf("isoWeek").format("YYYY-MM-DD"),
      toDate: anchor.clone().endOf("isoWeek").format("YYYY-MM-DD"),
    };
  }
  if (calendarView === "month") {
    // isoWeek here for the same reason as the week branch: the month grid is Monday-first, so
    // Sunday-based weeks left the grid's leading/trailing cells outside the fetch window.
    return {
      fromDate: anchor.clone().startOf("month").startOf("isoWeek").format("YYYY-MM-DD"),
      toDate: anchor.clone().endOf("month").endOf("isoWeek").format("YYYY-MM-DD"),
    };
  }
  return { fromDate: anchor.format("YYYY-MM-DD"), toDate: anchor.format("YYYY-MM-DD") };
};

// Identity of the newest fetchSchedules call. Replies from any older call are dropped wholesale:
// two rapid filter changes each start a replacing fetch, and without this the last reply to land
// wins the board even when it belongs to a stale call. Only the newest call may write results or
// lower the flag it raised. Kept separate from orders.actions.js's counter - the two thunks are
// independent races.
let newestScheduleFetchId = 0;

export const fetchSchedules = (range) => async (dispatch, getState) => {
  const fetchId = ++newestScheduleFetchId;
  const state = getState();
  const selectedUsers = userFilterSelectors.getSelectedUsers(state);
  const selectedLabels = labelFilterSelectors.getSelectedLabels(state);
  const selectedStatuses = statusFilterSelectors.getSelectedStatuses(state);
  const selectedProgress = progressFilterSelectors.getSelectedProgress(state);
  const search = searchFilterSelectors.getSearch(state);
  const users = userFilterSelectors.getUsers(state);
  const labels = labelFilterSelectors.getLabels(state);
  const statuses = statusFilterSelectors.getStatuses(state);
  const requiredStartDate = requiredDateSelectors.getRequiredStartDate(state);
  const requiredEndDate = requiredDateSelectors.getRequiredEndDate(state);

  const filters = {};

  // Empty a server-side group (assignee/labels/status) => empty board. The backend reads an empty
  // list as "no filter", so the client short-circuits. This holds during a search too.
  if (
    hasEmptiedServerFilterGroup({
      users,
      selectedUsers,
      labels,
      selectedLabels,
      statuses,
      selectedStatuses,
    })
  ) {
    dispatch(setOrderInCalendar([]));
    return;
  }

  // A search stays within the active filters (CUS-574, reversing the earlier "search supersedes
  // filters" decision): the query goes out with every group, Progress included. The back arrow
  // clears `search` and the next fetch sends the groups alone.
  if (search !== "") {
    filters.search = search;
  }

  if (selectedLabels.length > 0) {
    filters.labels = selectedLabels;
  }

  if (selectedUsers.length > 0) {
    filters.assignees = selectedUsers;
  }

  if (selectedStatuses.length > 0) {
    filters.statuses = selectedStatuses;
  }

  // Progress is always sent (unlike the other groups): the empty selection must reach the server
  // as `progress: []` so the board comes back empty. Default all-on returns the full board.
  filters.progress = selectedProgress;

  // Required date: single-day range. Reaches the backend as required_start_date /
  // required_end_date (createApiV2 snake-cases the body). Only sent when a date is picked.
  if (requiredStartDate) {
    filters.requiredStartDate = requiredStartDate;
  }
  if (requiredEndDate) {
    filters.requiredEndDate = requiredEndDate;
  }

  // No explicit range (a filter/search refetch): derive the whole visible window from the view.
  const visibleRange = getVisibleRange(
    selectors.getCalendarView(state),
    range?.fromDate ?? selectors.getFromDate(state)
  );
  const fromDate = range?.fromDate
    ? moment(range.fromDate).format("YYYY-MM-DD")
    : visibleRange.fromDate;
  const toDate = range?.toDate ? moment(range.toDate).format("YYYY-MM-DD") : visibleRange.toDate;

  try {
    dispatch(setIsFetchingSchedules(true));
    const { data } = await calendarApi.getAssignSchedule({
      from_date: moment
        .tz(`${fromDate}T00:00:00`, date.getCurrentCompanyTimezone())
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss[Z]"),
      to_date: moment
        .tz(`${toDate}T23:59:59`, date.getCurrentCompanyTimezone())
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss[Z]"),
      ...filters,
    })();

    if (fetchId !== newestScheduleFetchId) {
      return;
    }

    dispatch(setOrderInCalendar(data));
  } catch (error) {
    if (fetchId !== newestScheduleFetchId) {
      return;
    }
    handleError(error, dispatch);
  } finally {
    if (fetchId === newestScheduleFetchId) {
      dispatch(setIsFetchingSchedules(false));
    }
  }
};

// The panel fetched its entries when the drawer opened, so creating one leaves it a request behind.
const refreshOpenOrderPanel = (dispatch, getState, orderUid) => {
  const state = getState();

  if (drawerSelectors.getIsOpen(state) && drawerSelectors.getActiveOrder(state)?.uid === orderUid) {
    dispatch(drawerActions.fetchOrderDetails(orderUid));
  }
};

export const dropOrderInCalendar = (data) => async (dispatch, getState) => {
  try {
    dispatch(setIsCreatingSchedule(true));

    const payload = {
      order: data.uid,
      user: data.resource,
    };

    payload.start_time = date.toUtcDateTime({
      date: data.start,
      timezone: date.getCurrentCompanyTimezone(),
    });

    payload.end_time = date.toUtcDateTime({
      date: data.end,
      timezone: date.getCurrentCompanyTimezone(),
    });

    const response = await calendarApi.postAssignSchedule(payload)();
    dispatch(
      addScheduleEntry({
        ...response.data,
        // The create response's order omits combinedStatusString, status and total_value; carry them
        // from the dragged order so the entry colours by workflow status and shows its order total
        // straight away (the drawer reads totalValue), not only after a refetch.
        order: {
          ...response.data.order,
          combinedStatusString: data.combinedStatusString,
          status: data.status,
          totalValue: data.totalValue,
        },
      })
    );
    // The drop may have moved the order Unscheduled -> Partial or -> Scheduled. Only the server
    // knows which, so refetch rather than adjusting the badges here.
    dispatch(orderActions.refreshLoadedOrders());

    refreshOpenOrderPanel(dispatch, getState, data.uid);
  } catch (error) {
    handleError(error, dispatch);
  } finally {
    dispatch(setIsCreatingSchedule(false));
  }
};

export const updateOrderInCalendar =
  ({ uid, data }) =>
  async (dispatch, getState) => {
    const schedules = selectors.getSchedules(getState());
    const previousEntry = schedules.find((s) => s.uid === uid);

    const payload = {
      uid: data.uid,
      user: data.user,
    };

    payload.start_time = date.toUtcDateTime({
      date: data.start,
      timezone: date.getCurrentCompanyTimezone(),
    });

    payload.end_time = date.toUtcDateTime({
      date: data.end,
      timezone: date.getCurrentCompanyTimezone(),
    });

    // Optimistic: the scheduler is controlled, so the tile won't move until Redux updates.
    // Patch only this entry (drag feels instant) and roll back to its prior fields on failure.
    dispatch(
      updateScheduleEntry(uid, {
        user: data.user,
        startTime: payload.start_time,
        endTime: payload.end_time,
      })
    );

    try {
      await calendarApi.patchAssignSchedule({ uid, data: payload })();
      // The entry moved to a different lane, so a different user now owns it and the order's
      // coverage may have changed.
      dispatch(orderActions.refreshLoadedOrders());
    } catch (error) {
      if (previousEntry) {
        dispatch(
          updateScheduleEntry(uid, {
            user: previousEntry.user,
            startTime: previousEntry.startTime,
            endTime: previousEntry.endTime,
          })
        );
      }
      handleError(error, dispatch);
    }
  };

export const retimeTimeSlot =
  ({ uid, start, end }) =>
  async (dispatch, getState) => {
    const schedules = selectors.getSchedules(getState());
    const previousEntry = schedules.find((s) => s.uid === uid);
    const timezone = date.getCurrentCompanyTimezone();
    const startTime = date.toUtcDateTime({ date: start, timezone });
    const endTime = date.toUtcDateTime({ date: end, timezone });

    dispatch(updateScheduleEntry(uid, { startTime, endTime }));

    try {
      await calendarApi.patchAssignSchedule({
        uid,
        data: { start_time: startTime, end_time: endTime },
      })();
    } catch (error) {
      if (previousEntry) {
        dispatch(
          updateScheduleEntry(uid, {
            startTime: previousEntry.startTime,
            endTime: previousEntry.endTime,
          })
        );
      }
      handleError(error, dispatch);
    }
  };

// Update status of calendar card
export const changeOrderStatus =
  ({ orderUid, endIndex, statusUid }) =>
  async (dispatch) => {
    try {
      await workflowApi.postMoveCardOrder({
        order_uid: orderUid,
        new_index: endIndex,
        new_column_uid: statusUid,
      })();
    } catch (error) {
      handleError(error, dispatch);
    }
  };
// For delivered and picked up status
export const changeOrderExtendedStatus =
  ({ orderUid, isReceived }) =>
  async (dispatch) => {
    try {
      await workflowApi.postMarkOrder({
        order_uid: orderUid,
        is_received: isReceived,
      })();
    } catch (error) {
      handleError(error, dispatch);
    }
  };
export const setDateToPreviousDay = () => async (dispatch, getState) => {
  const state = getState();
  const currentDate = selectors.getFromDate(state);

  await dispatch(setFromDate(new Date(currentDate.getTime() - 24 * 60 * 60 * 1000)));
  await dispatch(setToDate(new Date(currentDate.getTime() - 24 * 60 * 60 * 1000)));
};

export const setDateToNextDay = () => async (dispatch, getState) => {
  const state = getState();
  const currentDate = selectors.getFromDate(state);

  await dispatch(setFromDate(new Date(currentDate.getTime() + 24 * 60 * 60 * 1000)));
  await dispatch(setToDate(new Date(currentDate.getTime() + 24 * 60 * 60 * 1000)));
};

export const removeTimeSlot = (uid) => async (dispatch) => {
  // Guard against deleting an unsaved drag-created placeholder (no real schedule
  // uid), which would otherwise call DELETE .../assignment_schedule_entries/undefined/
  // and surface an "Internal server error" (CUS-416).
  if (!uid) {
    return;
  }

  try {
    await calendarApi.deleteAssignSchedule(uid)();

    dispatch(removeScheduleEntry(uid));

    // Deleting an entry can move the order Scheduled -> Partial, or Partial -> Unscheduled if it
    // was the last one. Refetch instead of patching the rail by hand: the three-line rule lives on
    // the server and must not be re-derived here.
    dispatch(orderActions.refreshLoadedOrders());

    dispatch(alertActions.createAlertAction(nanoid(), "Timeslot deleted", true, "danger"));
  } catch (error) {
    handleError(error, dispatch);
  }
};

// True when the progress filter is narrowing the board (not "all" selected) - the drop/move
// paths' rail refresh mirrors this: only a narrowed filter can make an entry leave/enter the set.
const isProgressFilterActive = (state) =>
  progressFilterSelectors.getSelectedProgress(state).length !== PROGRESS_VALUES.length;

export const markTimeSlotStatus =
  ({ uid, isCompleted }) =>
  async (dispatch, getState) => {
    try {
      await calendarApi.patchAssignSchedule({
        uid,
        data: { is_completed: isCompleted },
      })();

      dispatch(
        updateScheduleEntry(uid, {
          isCompleted,
          // D6 (two-way): completing an entry clears any blocked state + reason,
          // mirroring the backend so the board updates without a refetch.
          ...(isCompleted ? { isBlocked: false, blockReason: null, blockReasonNote: null } : {}),
        })
      );

      // The optimistic patch above keeps the tile responsive; the progress filter and the rail's
      // schedule-state counts are server-derived and go stale without a refetch.
      if (isProgressFilterActive(getState())) {
        dispatch(fetchSchedules());
      }
      dispatch(orderActions.refreshLoadedOrders());
    } catch (error) {
      handleError(error, dispatch);
    }
  };

// F08 mark/unblock. Mirrors markTimeSlotStatus: PATCH the entry then patch it in the reducer so the
// board updates without a refetch. Right-click blocks with no reason (blockReason null); the drawer
// supplies the reason. The backend enforces "Other" note required + two-way exclusion; we mirror
// the exclusion here (blocking clears completion).
export const markTimeSlotBlocked =
  ({ uid, isBlocked, blockReason = null, blockReasonNote = null }) =>
  async (dispatch, getState) => {
    try {
      await calendarApi.patchAssignSchedule({
        uid,
        data: {
          is_blocked: isBlocked,
          block_reason: blockReason,
          block_reason_note: blockReasonNote,
        },
      })();

      dispatch(
        updateScheduleEntry(uid, {
          isBlocked,
          blockReason: isBlocked ? blockReason : null,
          blockReasonNote: isBlocked ? blockReasonNote : null,
          ...(isBlocked ? { isCompleted: false } : {}),
        })
      );

      // The optimistic patch above keeps the tile responsive; the progress filter and the rail's
      // schedule-state counts are server-derived and go stale without a refetch.
      if (isProgressFilterActive(getState())) {
        dispatch(fetchSchedules());
      }
      dispatch(orderActions.refreshLoadedOrders());
    } catch (error) {
      handleError(error, dispatch);
    }
  };

// F07 manual colour override write path. Each calls its endpoint then optimistically remaps the
// schedules (flattenSchedule spreads overrideColour/overrideClearedAt onto event.original, so the
// board recolours without a refetch). Clear-all is SOFT: keep overrideColour, stamp
// overrideClearedAt so the resolver falls back to source; restore un-stamps it.
export const setEntryColour =
  ({ uid, colour }) =>
  async (dispatch) => {
    try {
      await calendarApi.postEntryColour({ uid, colour })();
      dispatch(updateScheduleEntry(uid, { overrideColour: colour, overrideClearedAt: null }));
    } catch (error) {
      handleError(error, dispatch);
    }
  };

export const clearEntryColour =
  ({ uid }) =>
  async (dispatch) => {
    try {
      await calendarApi.postClearEntryColour({ uid })();
      dispatch(updateScheduleEntry(uid, { overrideColour: null, overrideClearedAt: null }));
    } catch (error) {
      handleError(error, dispatch);
    }
  };

export const clearAllEntryColours = () => async (dispatch) => {
  try {
    await calendarApi.deleteEntryColourOverrides()();
    dispatch(
      updateAllScheduleEntries(
        { overrideClearedAt: new Date().toISOString() },
        { field: "overrideColour" }
      )
    );
  } catch (error) {
    handleError(error, dispatch);
  }
};

export const restoreEntryColours = () => async (dispatch) => {
  try {
    await calendarApi.postRestoreEntryColours()();
    dispatch(updateAllScheduleEntries({ overrideClearedAt: null }));
  } catch (error) {
    handleError(error, dispatch);
  }
};

// Undo behind the clear-all alert. A failed restore keeps the alert up so the user can try again -
// restoreEntryColours has already reported the error, so re-alerting here would say it twice.
export const undoClearAllEntryColours =
  ({ id }) =>
  async (dispatch) => {
    try {
      await dispatch(restoreEntryColours());
      dispatch(plannerAlertActions.dismissAlert({ id }));
      return true;
    } catch {
      return false;
    }
  };

// Company-wide count of active overrides for the clear-all confirm ("Returns all N ..."). It's a
// read shown only while the modal is open, so it returns the number for local state rather than
// storing it. Shares the backend queryset with clearAllEntryColours, so N equals what it will clear.
export const fetchColourOverridesCount = () => async (dispatch) => {
  try {
    const { data } = await calendarApi.getEntryColourOverridesCount()();
    return data.count;
  } catch (error) {
    handleError(error, dispatch);
    return null;
  }
};

// Fetch one board schedule entry by uid, flattened like the board rows, so "View on planner" can open
// the drawer for an assignee whose entry is scheduled OUTSIDE the loaded date window (and so is not in
// the in-memory schedules). Returns null on error (handleError shows an alert).
export const fetchScheduleEntry = (uid) => async (dispatch) => {
  try {
    const { data } = await calendarApi.getScheduleEntry(uid)();
    return selectors.flattenSchedule(data, date.getCurrentCompanyTimezone());
  } catch (error) {
    handleError(error, dispatch);
    return null;
  }
};

export const createAdditionalTimeSlot = (uid) => async (dispatch, getState) => {
  try {
    const state = getState();
    const schedule = selectors.getSchedules(state).find((s) => s.uid === uid);

    if (schedule) {
      const payload = {
        order: schedule?.order?.uid ?? null,
        user: schedule.user,
      };

      payload.start_time = schedule.startTime;
      payload.end_time = schedule.endTime;

      const response = await calendarApi.postAssignSchedule(payload)();
      dispatch(addScheduleEntry(response.data));
      refreshOpenOrderPanel(dispatch, getState, schedule?.order?.uid);
    }
  } catch (error) {
    handleError(error, dispatch);
  }
};

export const getOrder =
  ({ orderUid, customerUid }) =>
  () =>
    ordersApi.getOrder({ orderUid, customerUid })();

export const setSelectedResourcesForDisplay = (id) => (dispatch, getState) => {
  const state = getState();

  const selectedResources = selectors.getSelectedResources(state);
  const allUsers = selectors.getUsers(state);

  if (id === "" && selectedResources.length === allUsers.length) {
    return dispatch(setSelectedUsers([]));
  }

  if (id === "" && (selectedResources.length === 0 || selectedResources.length < allUsers.length)) {
    return dispatch(setSelectedUsers(allUsers.map((r) => r.id)));
  }

  if (id === "" && selectedResources.length === 0) {
    return dispatch(setSelectedUsers(allUsers.map((r) => r.id)));
  }

  if (selectedResources.includes(id)) {
    return dispatch(setSelectedUsers(selectedResources.filter((userId) => userId !== id)));
  }

  return dispatch(setSelectedUsers([...selectedResources, id]));
};

export const selectAllResources = () => (dispatch, getState) => {
  const state = getState();
  const users = selectors.getUsers(state);

  dispatch(setSelectedUsers(users.map((r) => parseInt(r.id))));
};

// "View on planner" reveals an assignee's lane the row-visibility filter is
// hiding, then the drawer opens on it. selectedUsers holds numbers, so the id is
// coerced to match; usersToResources reveals the lane via selected.includes(u.id).
// Add-only: setSelectedResourcesForDisplay toggles, so when the lane is already
// visible we skip - dispatching would hide it.
export const revealResourceForDisplay = (id) => (dispatch, getState) => {
  const state = getState();
  const resourceId = Number(id);
  const selectedResources = selectors.getSelectedResources(state);

  if (selectedResources.includes(resourceId)) {
    return;
  }

  dispatch(setSelectedResourcesForDisplay(resourceId));
};

export const setShowCustomEventModal = (payload) => (dispatch) => {
  dispatch({
    type: SET_SHOW_CUSTOM_EVENT_MODAL,
    payload,
  });
};

export const setInitialCustomEventData = (payload) => (dispatch) => {
  dispatch({
    type: SET_INTITIAL_CUSTOM_EVENT_DATA,
    payload,
  });
};

export const setShowDeleteCustomEventWarningModal = (payload) => (dispatch) => {
  dispatch({
    type: SET_SHOW_DELETE_CUSTOM_EVENT_WARNING_MODAL,
    payload,
  });
};

export const setShowEditCustomEventWarningModal = (payload) => (dispatch) => {
  dispatch({
    type: SET_SHOW_EDIT_CUSTOM_EVENT_WARNING_MODAL,
    payload,
  });
};

export const setDeleteScope = (payload) => (dispatch) => {
  dispatch({
    type: SET_DELETE_SCOPE,
    payload,
  });
};

export const setEditScope = (payload) => (dispatch) => {
  dispatch({
    type: SET_EDIT_SCOPE,
    payload,
  });
};
